/**
 * LineageHeritage — TEM-989 "Radio lineage leg" (read-only render).
 *
 * Renders the per-album heritage a Tempest DJ set carried up in
 * `stream.metadata.lineage` (produced from Record's 294k-edge lineage graph).
 * Purely presentational: it renders what the upload sent and never re-queries a
 * graph. Renders nothing when there is no lineage (defensive — most streams
 * have none). The DJ-side producer is `apps/dj/src-tauri/src/upload/lineage.rs`.
 *
 * TEM-1591 (lineage W14): heritage on this public page is CREDITS ONLY (Rich,
 * TEM-1574). A chip is drawn only for a leg DJ stamped `tier: 'credited'`, and
 * it is labelled in the album panel's words (`utils/heritageLegs.ts`), never
 * by its edge_type: by the person's role when DJ stamped one ("Composer",
 * "on bass"), else "Credited" (TEM-1591 follow-up). A set published before the
 * stamp carried sound-alikes and an embedding cluster as "heritage"; its
 * untiered legs are not drawn (Rich accepted this, 2026-09-25).
 */

import React from 'react';
import { LineageEntry } from '../services/streams';
import { heritageLabel, heritageSource, isPublishedHeritage } from '../utils/heritageLegs';
import { narrativeProse } from '../utils/narrativeState';
import './LineageHeritage.css';

interface LineageHeritageProps {
  lineage?: LineageEntry[];
}

interface AlbumGroup {
  album: string;
  /** The album's story, if a leg carried PROSE (never the failure marker). */
  prose?: string;
  entries: LineageEntry[];
}

const LineageHeritage: React.FC<LineageHeritageProps> = ({ lineage }) => {
  // `metadata.lineage` is a schemaless cloud field — guard against a non-array
  // truthy value (a bare `.length` check would pass for an object and then throw
  // in the for..of below, white-screening the whole stream page).
  if (!Array.isArray(lineage) || lineage.length === 0) {
    return null;
  }

  // Group legs by album, preserving first-seen order. A narrative-only leg
  // (edge_type === 'narrative') contributes the album narrative but no chip.
  //
  // KNOWN MINOR LIMITATION (TEM-989): two DISTINCT albums that share a title —
  // or that both lack a DJ-side title (both key to 'Unknown album') — merge into
  // one group and the second's narrative is dropped. The wire format carries
  // only the denormalized title as a grouping key, so the render layer cannot
  // disambiguate; the robust fix is a per-album index stamped on the leg by the
  // Rust producer. Rare in practice (collection-synced tracks carry album titles)
  // → tracked as a follow-up rather than churning the keystone's wire schema.
  const groups: AlbumGroup[] = [];
  const byAlbum = new Map<string, AlbumGroup>();
  for (const leg of lineage) {
    // Skip a malformed entry (schemaless cloud field) rather than throwing.
    if (!leg || typeof leg !== 'object') {
      continue;
    }
    const key = leg.album || 'Unknown album';
    let group = byAlbum.get(key);
    if (!group) {
      group = { album: key, entries: [] };
      byAlbum.set(key, group);
      groups.push(group);
    }
    // TEM-1568: this is a public page, and `narrative_text` arrives verbatim
    // from the DJ upload — it can be the stored failure marker
    // ("⚠️ Narrative generation failed: …"), which printed here as the
    // album's heritage. Only prose is kept, and a marker on one leg does not
    // claim the slot a later leg's prose should fill.
    const prose = narrativeProse(leg.narrative_text);
    if (prose && !group.prose) {
      group.prose = prose;
    }
    // TEM-1591: credits only — never a sound-alike, a listener pair, or a
    // leg nobody stamped (a set published before the tier existed).
    if (leg.edge_type !== 'narrative' && isPublishedHeritage(leg)) {
      group.entries.push(leg);
    }
  }

  // An album whose only leg carried the failure marker has neither prose nor
  // chips: nothing to show, so it is neither drawn nor counted (TEM-1568).
  const shown = groups.filter((g) => g.prose || g.entries.length > 0);

  if (shown.length === 0) {
    return null;
  }

  return (
    <section className="v2-section lineage-heritage" aria-label="Heritage">
      <h2 className="v2-section-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="3" />
          <path d="M12 8v4M12 12l-5 4M12 12l5 4" />
          <circle cx="7" cy="19" r="2" />
          <circle cx="17" cy="19" r="2" />
        </svg>
        Heritage
        <span className="v2-badge-count">
          {shown.length} album{shown.length !== 1 ? 's' : ''}
        </span>
      </h2>

      <div className="lineage-heritage-groups">
        {shown.map((group, gi) => (
          <div key={gi} className="lineage-heritage-album">
            <h3 className="lineage-heritage-album-title">{group.album}</h3>
            {group.prose && (
              <p className="lineage-heritage-narrative">{group.prose}</p>
            )}
            {group.entries.length > 0 && (
              <div className="lineage-heritage-chips">
                {group.entries.map((entry, ei) => (
                  <span
                    key={ei}
                    className="lineage-heritage-chip"
                    title={`${heritageLabel(entry)} · via ${heritageSource(entry.source)}`}
                  >
                    <span className="lineage-heritage-chip-rel">{heritageLabel(entry)}</span>
                    <span className="lineage-heritage-chip-target">{entry.target_name}</span>
                    <span className="lineage-heritage-chip-source">{heritageSource(entry.source)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default LineageHeritage;
